// Visual language follows the other system pages (Departments, Broadcasts,
// Admin Accounts): a glass header card tucked under the topbar, glass section
// cards with an icon title, Add-Employee-modal style fields and modals, and
// the #2e7cba accent. Every key the components already read is kept.
import { unifiedControlStyle } from '../../../components/ReusableDropdown';

export const BRAND = '#2e7cba';
export const BRAND_DARK = '#256a9f';
export const DANGER = '#ef4444';

export const getStyles = (theme) => {
  const dark = theme !== 'light';

  const c = {
    text: dark ? '#ffffff' : '#0f172a',
    body: dark ? 'rgba(255, 255, 255, 0.85)' : '#334155',
    muted: dark ? 'rgba(255, 255, 255, 0.6)' : '#64748b',
    faint: dark ? 'rgba(255, 255, 255, 0.4)' : '#94a3b8',
    border: dark ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0',
    rowBorder: dark ? 'rgba(255, 255, 255, 0.06)' : '#f1f5f9',
    softBg: dark ? 'rgba(255, 255, 255, 0.03)' : '#f8fafc',
    chipBg: dark ? 'rgba(46, 124, 186, 0.2)' : 'rgba(46, 124, 186, 0.08)',
    chipText: dark ? '#8cc4ee' : BRAND,
  };

  // Same glass surface as Mo3tiStatCard / the Departments cards.
  const glass = {
    background: dark ? 'rgba(255, 255, 255, 0.02)' : 'rgba(255, 255, 255, 0.9)',
    border: dark ? '1px solid rgba(255, 255, 255, 0.15)' : '1px solid rgba(0, 0, 0, 0.05)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    boxShadow: dark
      ? '0 8px 32px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
      : '0 8px 32px rgba(0, 0, 0, 0.08)',
  };

  // Field look of the Add Employee modal.
  const field = {
    ...unifiedControlStyle(theme, false),
    padding: '12px 14px',
    borderRadius: '10px',
    border: dark ? '1.5px solid rgba(255,255,255,0.12)' : '1.5px solid rgba(0,0,0,0.08)',
    background: dark ? 'rgba(15, 23, 42, 0.4)' : 'rgba(248, 250, 252, 0.9)',
    color: dark ? '#e6eef8' : '#0f172a',
    transition: 'all 300ms ease',
  };

  const btnBase = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '11px 18px',
    borderRadius: '12px',
    fontSize: '14px',
    fontWeight: 700,
    fontFamily: 'inherit',
    whiteSpace: 'nowrap',
    border: '1px solid transparent',
    transition: 'all 0.2s ease',
    outline: 'none',
    cursor: 'pointer',
  };
  const primary = { ...btnBase, background: BRAND, color: '#ffffff' };
  const secondary = {
    ...btnBase,
    background: dark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(15, 23, 42, 0.05)',
    color: c.body,
    borderColor: c.border,
  };

  const label = {
    display: 'block',
    fontSize: '12px',
    fontWeight: 700,
    color: dark ? '#94a3b8' : '#475569',
    marginBottom: '10px',
    textTransform: 'uppercase',
    letterSpacing: '0.6px',
  };

  return {
    colors: c,
    brand: BRAND,

    // ── page ──
    container: { color: c.text },
    header: {
      ...glass,
      borderRadius: '24px',
      padding: '32px',
      marginTop: '-36px',
      marginBottom: '24px',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      gap: '24px',
      animation: 'cfgSlideDown 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
    },
    headerText: { minWidth: 0, flex: 1 },
    pageTitle: {
      fontSize: '28px',
      fontWeight: 700,
      letterSpacing: '-0.5px',
      color: dark ? '#f1f5f9' : '#1e293b',
      margin: 0,
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    },
    pageSubtitle: {
      fontSize: '15px',
      fontWeight: 500,
      color: dark ? '#cbd5e1' : '#64748b',
      margin: '12px 0 0 0',
      lineHeight: 1.6,
    },

    // Glass section card (company list, configuration panel).
    card: {
      ...glass,
      borderRadius: '16px',
      padding: '24px',
      marginBottom: '24px',
    },
    sectionHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      gap: '12px',
      marginBottom: '20px',
      paddingBottom: '16px',
      borderBottom: dark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.08)',
    },
    sectionHeading: {
      fontSize: '18px',
      fontWeight: 700,
      color: c.text,
      margin: 0,
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
    },
    sectionSubheading: { fontSize: '13.5px', color: c.muted, margin: '4px 0 0 0' },
    title: {
      fontSize: '22px',
      fontWeight: 700,
      color: c.text,
      margin: '0 0 24px 0',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      letterSpacing: '-0.3px',
    },
    gridContainer: {
      display: 'grid',
      gridTemplateColumns: '320px minmax(0, 1fr)',
      gap: '24px',
      alignItems: 'start',
    },
    countPill: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: '24px',
      height: '24px',
      padding: '0 8px',
      borderRadius: '999px',
      fontSize: '12px',
      fontWeight: 700,
      background: c.chipBg,
      color: c.chipText,
    },

    // ── company list ──
    companyList: { maxHeight: '75vh', overflowY: 'auto', paddingInlineEnd: '4px' },
    companyCard: (isSelected) => ({
      padding: '12px 14px',
      border: isSelected ? `1.5px solid ${BRAND}` : `1px solid ${c.border}`,
      borderRadius: '12px',
      marginBottom: '8px',
      background: isSelected ? c.chipBg : dark ? 'rgba(255,255,255,0.02)' : '#ffffff',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    }),
    companyName: {
      fontSize: '14px',
      fontWeight: 700,
      color: c.text,
      marginBottom: '2px',
      lineHeight: 1.3,
    },
    companyStatus: (isActive) => ({
      fontSize: '11.5px',
      color: isActive ? '#10b981' : '#f87171',
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      fontWeight: 600,
    }),

    // ── configuration panel ──
    permissionsContainer: {
      ...glass,
      borderRadius: '16px',
      padding: '24px',
      minHeight: '400px',
      minWidth: 0,
    },
    panelHeader: {
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: '12px',
      marginBottom: '20px',
      paddingBottom: '16px',
      borderBottom: dark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.08)',
    },
    panelAvatar: {
      width: '46px',
      height: '46px',
      borderRadius: '12px',
      background: BRAND,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#fff',
      fontSize: '15px',
      fontWeight: 800,
      flexShrink: 0,
    },
    panelTitle: { fontSize: '19px', fontWeight: 700, color: c.text, margin: 0, overflowWrap: 'anywhere' },
    panelSubtitle: { fontSize: '13px', color: c.muted, margin: '3px 0 0 0' },

    // A configuration block inside the panel (splash, subscription, code, status).
    subCard: {
      background: dark ? 'rgba(255, 255, 255, 0.02)' : '#ffffff',
      border: `1px solid ${c.border}`,
      borderRadius: '14px',
      padding: '20px',
      marginBottom: '20px',
    },
    subCardHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '12px',
      flexWrap: 'wrap',
      marginBottom: '18px',
      paddingBottom: '14px',
      borderBottom: `1px solid ${c.rowBorder}`,
    },
    subCardTitleRow: { display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 },
    subCardIcon: {
      width: '38px',
      height: '38px',
      borderRadius: '10px',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      background: c.chipBg,
      color: c.chipText,
    },
    subCardTitle: { fontSize: '15.5px', fontWeight: 700, color: c.text, margin: 0 },
    subCardSubtitle: { fontSize: '12.5px', color: c.muted, margin: '3px 0 0 0', lineHeight: 1.5 },
    // Blue group heading (Add Employee modal's SectionTitle).
    groupTitle: {
      fontSize: '13px',
      fontWeight: 800,
      color: dark ? '#60a5fa' : BRAND,
      borderInlineStart: `3px solid ${dark ? 'rgba(96, 165, 250, 0.6)' : BRAND}`,
      paddingInlineStart: '12px',
      margin: '22px 0 14px',
      textTransform: 'uppercase',
      letterSpacing: '0.6px',
    },
    fieldLabel: label,
    field,
    hint: { fontSize: '12.5px', color: c.muted, margin: '8px 0 0', lineHeight: 1.6 },
    fieldGrid: (min = 200) => ({
      display: 'grid',
      gridTemplateColumns: `repeat(auto-fit, minmax(min(${min}px, 100%), 1fr))`,
      gap: '16px',
    }),

    sectionTitle: {
      fontSize: '13px',
      fontWeight: 800,
      color: dark ? '#60a5fa' : BRAND,
      marginBottom: '12px',
      marginTop: '28px',
      borderInlineStart: `3px solid ${dark ? 'rgba(96, 165, 250, 0.6)' : BRAND}`,
      paddingInlineStart: '12px',
      textTransform: 'uppercase',
      letterSpacing: '0.6px',
    },
    permissionItem: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '11px 14px',
      marginBottom: '6px',
      borderRadius: '10px',
      background: dark ? 'rgba(255,255,255,0.04)' : '#ffffff',
      border: `1px solid ${c.border}`,
      transition: 'background 0.15s',
    },
    permissionLabel: { fontSize: '13.5px', fontWeight: 500, color: c.body },

    toggle: (isEnabled) => ({
      width: '44px',
      height: '24px',
      borderRadius: '12px',
      background: isEnabled ? BRAND : dark ? 'rgba(255,255,255,0.18)' : '#cbd5e1',
      position: 'relative',
      cursor: 'pointer',
      transition: 'background 0.25s',
      flexShrink: 0,
    }),
    toggleKnob: (isEnabled) => ({
      width: '18px',
      height: '18px',
      borderRadius: '50%',
      background: '#ffffff',
      position: 'absolute',
      top: '3px',
      left: isEnabled ? '23px' : '3px',
      transition: 'left 0.25s cubic-bezier(0.4,0,0.2,1)',
      boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
    }),

    // ── buttons ──
    button: primary,
    btnPrimary: (disabled = false) => ({ ...primary, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.55 : 1 }),
    btnSecondary: (disabled = false) => ({ ...secondary, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.55 : 1 }),
    secondaryButton: secondary,
    dangerSoftButton: {
      ...btnBase,
      background: dark ? 'rgba(239,68,68,0.18)' : 'rgba(254,226,226,0.9)',
      color: dark ? '#fca5a5' : '#b91c1c',
    },
    // Filled pill buttons (Departments / Payroll table actions).
    actionBtn: {
      padding: '9px 14px',
      borderRadius: '8px',
      border: 'none',
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '6px',
      fontSize: '12.5px',
      fontWeight: 600,
      fontFamily: 'inherit',
      transition: 'all 0.2s ease',
      background: dark ? 'rgba(71,85,105,0.6)' : 'rgba(226,232,240,0.8)',
      color: dark ? '#f1f5f9' : '#334155',
    },

    emptyState: {
      textAlign: 'center',
      padding: '80px 20px',
      color: c.faint,
    },
    message: (type) => ({
      padding: '12px 16px',
      borderRadius: '12px',
      marginBottom: '24px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      fontSize: '14px',
      fontWeight: 500,
      background:
        type === 'success' ? 'rgba(16,185,129,0.1)' : type === 'error' ? 'rgba(239,68,68,0.1)' : 'rgba(245,158,11,0.1)',
      border: `1px solid ${type === 'success' ? 'rgba(16,185,129,0.3)' : type === 'error' ? 'rgba(239,68,68,0.3)' : 'rgba(245,158,11,0.3)'}`,
      color: type === 'success' ? (dark ? '#6ee7b7' : '#047857') : type === 'error' ? (dark ? '#fca5a5' : '#b91c1c') : (dark ? '#fbbf24' : '#b45309'),
    }),
    notice: (tone = 'info') => ({
      display: 'flex',
      gap: '10px',
      alignItems: 'flex-start',
      padding: '12px 14px',
      borderRadius: '12px',
      fontSize: '13px',
      lineHeight: 1.6,
      background:
        tone === 'warning'
          ? dark ? 'rgba(245, 158, 11, 0.12)' : 'rgba(245, 158, 11, 0.08)'
          : tone === 'danger'
          ? dark ? 'rgba(239, 68, 68, 0.12)' : 'rgba(239, 68, 68, 0.08)'
          : c.chipBg,
      color:
        tone === 'warning' ? (dark ? '#fbbf24' : '#b45309') : tone === 'danger' ? (dark ? '#fca5a5' : '#b91c1c') : c.chipText,
      border: `1px solid ${
        tone === 'warning' ? 'rgba(245, 158, 11, 0.3)' : tone === 'danger' ? 'rgba(239, 68, 68, 0.3)' : 'rgba(46, 124, 186, 0.25)'
      }`,
    }),
    codeChip: {
      fontSize: '12px',
      fontWeight: 600,
      padding: '4px 10px',
      borderRadius: '8px',
      background: dark ? 'rgba(255,255,255,0.08)' : '#f1f5f9',
      color: c.text,
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
    },

    // ── splash & icon ──
    splashCard: {
      background: dark ? 'rgba(255, 255, 255, 0.02)' : '#ffffff',
      border: `1px solid ${c.border}`,
      borderRadius: '14px',
      padding: '20px',
      marginBottom: '20px',
    },
    splashHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'wrap',
      marginBottom: '18px',
      paddingBottom: '14px',
      borderBottom: `1px solid ${c.rowBorder}`,
      gap: '12px',
    },
    splashPreview: {
      width: '100%',
      maxHeight: '200px',
      minHeight: '120px',
      objectFit: 'contain',
      borderRadius: '12px',
      background: c.softBg,
      border: `1px solid ${c.border}`,
      padding: '8px',
      boxSizing: 'border-box',
    },
    splashInputRow: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(min(220px, 100%), 1fr))',
      gap: '16px',
    },
    splashInput: field,
    iconContainer: {
      marginTop: '22px',
      paddingTop: '18px',
      borderTop: `1px solid ${c.rowBorder}`,
    },
    iconHeaderRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '14px',
    },
    iconTitle: () => ({ fontSize: '14.5px', fontWeight: 700, color: c.text, margin: 0 }),
    iconSubtitle: () => ({ fontSize: '12.5px', color: c.muted, margin: '3px 0 0 0' }),
    iconPreview: {
      width: '72px',
      height: '72px',
      borderRadius: '14px',
      background: c.softBg,
      border: `1px solid ${c.border}`,
      padding: '10px',
      objectFit: 'contain',
    },
    iconPlaceholder: () => ({
      width: '72px',
      height: '72px',
      borderRadius: '14px',
      border: `1px solid ${c.border}`,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      gap: '5px',
      fontSize: '10.5px',
      lineHeight: 1.3,
      padding: '6px',
      boxSizing: 'border-box',
      color: c.faint,
      background: c.softBg,
    }),
    iconInputRow: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(min(220px, 100%), 1fr))',
      gap: '16px',
    },
    iconLabel: () => label,
    iconHint: () => ({ fontSize: '12.5px', color: c.muted, marginTop: '10px', lineHeight: 1.6 }),
    splashActions: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '10px',
      alignItems: 'center',
      marginTop: '20px',
      paddingTop: '16px',
      borderTop: `1px solid ${c.rowBorder}`,
    },

    // ── modals (same frame as the Add Employee modal) ──
    modal: {
      position: 'fixed',
      inset: 0,
      background: 'rgba(0, 0, 0, 0.6)',
      backdropFilter: 'blur(8px)',
      WebkitBackdropFilter: 'blur(8px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      zIndex: 9999,
      animation: 'cfgFadeIn 0.2s ease',
    },
    modalContent: {
      background: dark ? 'linear-gradient(135deg, #0f172a 0%, #1a2a3a 100%)' : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
      color: dark ? '#e6eef8' : '#0f172a',
      borderRadius: '16px',
      padding: '28px 32px',
      width: '100%',
      maxWidth: '560px',
      maxHeight: '90vh',
      overflowY: 'auto',
      boxSizing: 'border-box',
      scrollbarWidth: 'none',
      msOverflowStyle: 'none',
      border: dark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(148,163,184,0.15)',
      boxShadow: dark
        ? '0 25px 60px rgba(0,0,0,0.8), 0 0 1px rgba(255,255,255,0.1)'
        : '0 20px 50px rgba(0,0,0,0.12), 0 0 1px rgba(0,0,0,0.04)',
      animation: 'cfgSlideIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
    },
    modalHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '12px',
      marginBottom: '24px',
      paddingBottom: '16px',
      borderBottom: dark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)',
    },
    modalTitle: {
      margin: 0,
      fontSize: '22px',
      fontWeight: 800,
      letterSpacing: '-0.5px',
      background: dark ? 'linear-gradient(135deg, #fff 0%, #cbd5e1 100%)' : 'linear-gradient(135deg, #0f172a 0%, #2e7cba 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
    },
    modalLabel: label,
    closeButton: {
      width: '38px',
      height: '38px',
      borderRadius: '10px',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      background: dark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.05)',
      border: dark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(15,23,42,0.06)',
      color: dark ? '#e6eef8' : '#0f172a',
      cursor: 'pointer',
    },
    input: { ...field, marginBottom: '20px' },
    modalButtons: {
      display: 'flex',
      gap: '12px',
      justifyContent: 'flex-end',
      flexWrap: 'wrap',
      marginTop: '28px',
      paddingTop: '20px',
      borderTop: dark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)',
    },
    cancelButton: secondary,
    submitButton: primary,
  };
};

export const getConfigurationGlobalCss = (theme) => {
  const dark = theme !== 'light';
  return `
  .cfg-input:focus { border-color: rgba(46,124,186,0.6) !important; box-shadow: 0 0 0 3px rgba(46,124,186,0.12); }
  .cfg-input::placeholder { color: ${dark ? 'rgba(203,213,225,0.5)' : 'rgba(100,116,139,0.6)'}; }
  .cfg-input:disabled { opacity: 0.6; cursor: not-allowed; }
  .cfg-input[type="file"] { padding: 8px !important; cursor: pointer; }
  .cfg-input::file-selector-button {
    margin-inline-end: 12px; padding: 7px 14px; border: none; border-radius: 8px; cursor: pointer;
    font: inherit; font-size: 12.5px; font-weight: 600;
    background: ${dark ? 'rgba(46,124,186,0.3)' : 'rgba(46,124,186,0.12)'}; color: ${dark ? '#bfdbfe' : BRAND};
  }
  .cfg-btn-primary:hover:not(:disabled) { background: ${BRAND_DARK} !important; }
  .cfg-btn-secondary:hover:not(:disabled) { border-color: rgba(46,124,186,0.5) !important; color: ${dark ? '#ffffff' : BRAND} !important; }
  .cfg-action-btn:hover { transform: translateY(-2px); background: ${dark ? 'rgba(71,85,105,0.8)' : 'rgba(203,213,225,0.8)'} !important; }
  .cfg-company:hover { border-color: rgba(46,124,186,0.55) !important; }
  .cfg-modal::-webkit-scrollbar { display: none; }
  .cfg-scroll { scrollbar-width: none; }
  .cfg-scroll::-webkit-scrollbar { display: none; }
  @keyframes cfgFadeIn { from { opacity: 0; } to { opacity: 1; } }
  @keyframes cfgSlideIn { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
  @keyframes cfgSlideDown { from { opacity: 0; transform: translateY(-16px); } to { opacity: 1; transform: translateY(0); } }
  @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
  @media (prefers-reduced-motion: reduce) { .cfg-scope * { animation: none !important; transition: none !important; } }
`;
};
